import { useLocation, useNavigate } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import config from '@/config';
import Button from '@/design_system/Button';
import { Variant } from '@/design_system/Button/Button';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconDownload from '@/icons/Download.svg';
import IconInvoice from '@/icons/Invoice.svg';
import IconWarning from '@/icons/Warning.svg';
import { InvoiceFromGetMany, useCreateInvoice, useInvoices } from '@/models/invoice';
import { hasPermission } from '@/models/user';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useCurrentSession } from '@/services/auth';

export const ArticleWorkshopPayment = () => {
  const { article, request } = useArticleContext();
  const { currentSession } = useCurrentSession();

  const { data: { invoices } = { invoices: [] }, isLoading } = useInvoices(
    {
      articleId: article.id,
      originCompany: article.workshop?.companyId ?? undefined,
    },
    {
      enabled: !!article.workshop?.companyId,
    }
  );

  const canCreateInvoice = hasPermission(currentSession, 'create_invoice', [
    { organizationId: article.organizationId },
    { entityId: request.storeId },
  ]);

  return (
    <Stack gap="12px">
      <WorkshopPaymentStrategy />
      <WorkshopInvoices invoices={invoices} isLoading={isLoading} />
      {canCreateInvoice && !isLoading && invoices.length === 0 && <WorkshopUploadInvoice />}
    </Stack>
  );
};

const WorkshopPaymentStrategy = () => {
  const { article } = useArticleContext();

  if (!article.workshopPaymentStrategy) {
    return (
      <Stack>
        <Stack row gap="4px" alignItems="center">
          <IconWarning className="text-warning" />
          <InlineText size="200" color="warning">
            <Trans id="article.workshop-payment.strategy.label">Payment responsible</Trans>
          </InlineText>
        </Stack>
        <Paragraph weight="medium">-</Paragraph>
      </Stack>
    );
  }

  return (
    <Stack>
      <Paragraph size="200">
        <Trans id="article.workshop-payment.strategy.label">Payment responsible</Trans>
      </Paragraph>
      <Paragraph weight="medium">
        {
          {
            'store-cash-out': (
              <Trans id="article.workshop-payment.strategy.store-cash-out">The store</Trans>
            ),
            'handled-by-accounting-team': (
              <Trans id="article.workshop-payment.strategy.handled-by-accounting-team">
                The accounting department
              </Trans>
            ),
            'prolong-payout': (
              <Trans id="article.workshop-payment.strategy.prolong-payout">Prolong</Trans>
            ),
            'prolong-payin': '-', // Not supposed to happen
          }[article.workshopPaymentStrategy]
        }
      </Paragraph>
    </Stack>
  );
};

const WorkshopInvoices = ({
  invoices,
  isLoading,
}: {
  invoices: InvoiceFromGetMany[];
  isLoading: boolean;
}) => {
  const navigate = useNavigate();
  const { t } = useLingui();

  if (isLoading) {
    return null;
  }

  if (!invoices.length) {
    return (
      <Stack>
        <Stack row gap="4px" alignItems="center">
          <IconWarning className="text-warning" />
          <InlineText size="200" color="warning">
            <Trans id="article.workshop-payment.invoice.label">Invoice from workshop</Trans>
          </InlineText>
        </Stack>
        <Paragraph weight="medium">-</Paragraph>
      </Stack>
    );
  }

  return (
    <Stack>
      <Paragraph size="200">
        <Trans id="article.workshop-payment.invoice.label">Invoice from workshop</Trans>
      </Paragraph>
      {invoices.map((invoice) => (
        <Stack key={invoice.id} row alignItems="center" flexWrap="nowrap" gap="4px">
          <Paragraph weight="medium" ellipsis title={invoice.number}>
            {invoice.number}
          </Paragraph>
          <Button
            variant="secondary"
            size="small"
            iconOnly
            href={`${config.apiUrl}/invoices/${invoice.id}/media`}
            download
            ariaLabel={t({ id: 'article.workshop-payment.invoice.download', message: 'Download' })}
            tooltip={t({ id: 'article.workshop-payment.invoice.download', message: 'Download' })}
          >
            <IconDownload />
          </Button>
          <Button
            variant="secondary"
            size="small"
            iconOnly
            onPress={() =>
              navigate(`/accounting/invoices/${invoice.id}`, {
                state: {
                  onCancel: location.pathname,
                },
              })
            }
            ariaLabel={t({
              id: 'article.workshop-payment.invoice.details',
              message: 'View details',
            })}
            tooltip={t({
              id: 'article.workshop-payment.invoice.details',
              message: 'View details',
            })}
          >
            <IconInvoice success />
          </Button>
        </Stack>
      ))}
    </Stack>
  );
};

export const WorkshopUploadInvoice = ({ variant = 'secondary' }: { variant?: Variant }) => {
  const { article, request } = useArticleContext();
  const navigate = useNavigate();
  const location = useLocation();

  const { mutateAsync: createInvoice, isPending: isCreateInvoicePending } = useCreateInvoice();

  const handleCreateInvoice = async () => {
    const { id } = await createInvoice({
      paymentStrategy: article.workshopPaymentStrategy!,
      originCompanyId: article.workshop?.companyId ?? undefined,
      destinationCompanyId: request.afterSales?.companyId ?? undefined,
    });

    navigate(`/accounting/invoices/${id}`, {
      state: {
        onCancel: location.pathname,
      },
    });
  };

  return (
    <Button
      variant={variant}
      size="small"
      onPress={handleCreateInvoice}
      isLoading={isCreateInvoicePending}
    >
      <IconInvoice new />
      <Trans id="article.workshop-payment.upload-invoice">Upload workshop's invoice</Trans>
    </Button>
  );
};
